/**
 * Production-Ready - ExistingDebtTable (Builder Pattern)
 * Based on engine's existing debt output structure
 * Backend integration pending (see docs/planning/bond-calculator-app-plan.md)
 */

import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import type { ExistingDebtEntry, ExistingDebtPaymentRecord, ExistingDebtProject } from '~/types';
import { formatDateForTable, getYear } from '../../utils/date-utils';

interface ExistingDebtTableProps {
  /** Pre-formatted table data (optional - if provided, takes precedence over other data sources) */
  data?: ExistingDebtEntry[];
  /** Array of existing debt projects to display (converted to table format) */
  existingDebtProjects?: ExistingDebtProject[];
  /** Single combined debt service project (takes precedence over existingDebtProjects) */
  combinedDebtService?: ExistingDebtProject;
  /** Table title */
  title?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Whether to show summary statistics */
  showSummary?: boolean;
  /** Whether to show individual project tables in addition to combined view */
  showIndividualProjects?: boolean;
}

// Utility function to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Use centralized date formatting
const formatDate = formatDateForTable;

// Convert engine debt project to table data
const convertDebtProjectToTableData = (project: ExistingDebtProject): ExistingDebtEntry[] => {
  return project.existingDebtPaymentRecords.map((record, index) => ({
    id: `${project.id}-${index}`,
    name: project.name,
    paymentDate: record.paymentDate,
    principal: record.principalPayment,
    interest: record.principalInterest,
    totalPayment: record.totalPayment,
    fiscalYear: record.fiscalYearInformation.fiscalYearEndingDate
      ? getYear(record.fiscalYearInformation.fiscalYearEndingDate)
      : getYear(record.paymentDate),
    interestRate: record.interestRate,
    totalPaymentInterest: record.totalPaymentInterest,
    isFinalPayment: record.fiscalYearInformation.isFinalPayment,
  }));
};

export const ExistingDebtTable: React.FC<ExistingDebtTableProps> = ({
  data,
  existingDebtProjects = [],
  combinedDebtService,
  title = 'Existing Debt Payment Schedule',
  isLoading = false,
  error = null,
  showSummary = true,
  showIndividualProjects = false,
}) => {
  // Determine data source - priority: data > combinedDebtService > existingDebtProjects > empty
  const tableData = React.useMemo(() => {
    // Highest priority: pre-formatted data
    if (data && data.length > 0) {
      return data;
    }

    // Second priority: combined debt service (single aggregated project)
    if (combinedDebtService) {
      return convertDebtProjectToTableData(combinedDebtService);
    }

    // Third priority: individual existing debt projects (flattened)
    if (existingDebtProjects && existingDebtProjects.length > 0) {
      return existingDebtProjects.flatMap((project) => convertDebtProjectToTableData(project));
    }

    // Default: empty array
    return [];
  }, [data, existingDebtProjects, combinedDebtService]);

  // Column definitions for existing debt
  const columns: ColumnDefinition<ExistingDebtEntry>[] = [
    {
      key: 'paymentDate',
      header: 'Payment Date',
      accessor: (record) => formatDate(record.paymentDate),
      headerTextAlign: 'left',
      cellTextAlign: 'left',
    },
    {
      key: 'fiscalYear',
      header: 'Fiscal Year',
      accessor: (record) => record.fiscalYear.toString(),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'principal',
      header: 'Principal',
      accessor: (record) => formatCurrency(record.principal),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'interest',
      header: 'Interest',
      accessor: (record) => formatCurrency(record.interest),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'totalPayment',
      header: 'Total Payment',
      accessor: (record) => formatCurrency(record.totalPayment),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
  ];

  // Add project name column if showing multiple projects
  if (existingDebtProjects && existingDebtProjects.length > 1 && !combinedDebtService) {
    columns.unshift({
      key: 'name',
      header: 'Project',
      accessor: (record) => record.name,
      headerTextAlign: 'left',
      cellTextAlign: 'left',
    });
  }

  // Calculate summary statistics
  const summary = React.useMemo(() => {
    if (tableData.length === 0) return null;

    const totalPrincipal = tableData.reduce((sum, record) => sum + record.principal, 0);
    const totalInterest = tableData.reduce((sum, record) => sum + record.interest, 0);
    const totalPayments = tableData.reduce((sum, record) => sum + record.totalPayment, 0);
    const paymentCount = tableData.length;
    const yearRange =
      tableData.length > 0
        ? {
            start: Math.min(...tableData.map((r) => r.fiscalYear)),
            end: Math.max(...tableData.map((r) => r.fiscalYear)),
          }
        : null;

    return {
      totalPrincipal,
      totalInterest,
      totalPayments,
      paymentCount,
      yearRange,
    };
  }, [tableData]);

  // Create footer row data for totals
  const footerRowData = React.useMemo(() => {
    if (!summary) return null;

    const footerData: Partial<ExistingDebtEntry> = {
      paymentDate: 'TOTALS',
      fiscalYear: 0,
      principal: typeof summary.totalPrincipal === 'number' ? summary.totalPrincipal : 0,
      interest: typeof summary.totalInterest === 'number' ? summary.totalInterest : 0,
      totalPayment: typeof summary.totalPayments === 'number' ? summary.totalPayments : 0,
    };

    // Add project name column if showing multiple projects
    if (existingDebtProjects && existingDebtProjects.length > 1 && !combinedDebtService) {
      footerData.name = '';
    }

    return footerData;
  }, [summary, existingDebtProjects, combinedDebtService]);

  if (isLoading) {
    return (
      <Box>
        <Text>Loading existing debt data...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} bg="red.50" borderRadius="md" borderLeft="4px solid" borderColor="red.500">
        <Text color="red.700">{error}</Text>
      </Box>
    );
  }

  if (tableData.length === 0) {
    return (
      <Box>
        <Heading size="md" mb={4}>
          {title}
        </Heading>
        <Box p={4} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
          <Text color="blue.700">
            No existing debt payment data available. Create some debt projects to see the payment
            schedule.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <VStack align="stretch" gap={6}>
      <Box>
        <Heading size="md" mb={2}>
          {title}
        </Heading>
        {showSummary && summary && (
          <Box p={4} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" color="gray.600" mb={2}>
              Summary
            </Text>
            <Text fontSize="sm">
              <Text as="span" fontWeight="semibold">
                Total Principal:
              </Text>{' '}
              {formatCurrency(summary.totalPrincipal)} |{' '}
              <Text as="span" fontWeight="semibold">
                Total Interest:
              </Text>{' '}
              {formatCurrency(summary.totalInterest)} |{' '}
              <Text as="span" fontWeight="semibold">
                Total Payments:
              </Text>{' '}
              {formatCurrency(summary.totalPayments)}
            </Text>
            <Text fontSize="sm">
              <Text as="span" fontWeight="semibold">
                Payment Count:
              </Text>{' '}
              {summary.paymentCount} |{' '}
              {summary.yearRange && (
                <>
                  <Text as="span" fontWeight="semibold">
                    Years:
                  </Text>{' '}
                  {summary.yearRange.start} - {summary.yearRange.end}
                </>
              )}
            </Text>
          </Box>
        )}
      </Box>

      {/* Individual Projects Tables */}
      {showIndividualProjects && existingDebtProjects && existingDebtProjects.length > 1 && (
        <VStack align="stretch" gap={4}>
          <Text fontWeight="medium">Individual Debt Projects</Text>
          {existingDebtProjects.map((project) => {
            const projectData = convertDebtProjectToTableData(project);
            return (
              <Box key={project.id}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {project.name}
                </Text>
                <ReusableTable
                  data={projectData}
                  columns={columns.filter((col) => col.key !== 'name')} // Remove project name for individual tables
                  footerRow={
                    footerRowData
                      ? {
                          ...footerRowData,
                          name: undefined, // Remove name from footer for individual tables
                        }
                      : undefined
                  }
                />
              </Box>
            );
          })}
        </VStack>
      )}

      {/* Main Combined Table */}
      <Box>
        {showIndividualProjects && existingDebtProjects && existingDebtProjects.length > 1 && (
          <Text fontWeight="medium" mb={2}>
            Combined Debt Service
          </Text>
        )}
        <ReusableTable
          data={tableData as ExistingDebtEntry[]}
          columns={columns}
          footerRow={footerRowData}
        />
      </Box>
    </VStack>
  );
};

export type { ExistingDebtProject, ExistingDebtPaymentRecord, ExistingDebtEntry };
